// A password field with an eye.
//
// It exists because typing a password blind on a phone keyboard is where people give up: they
// mistype, get told the credentials are wrong, and cannot tell whether the password or the username
// was the problem. Being able to look is the fix, and it has to be the same control everywhere —
// the login screen, the signup form, and every place staff set somebody else's password.
//
// Two details that matter and are easy to get wrong:
//   • the toggle is NOT in the tab order, so tabbing from the password goes to the submit button
//     rather than into a control nobody wanted;
//   • it never submits — an unqualified <button> inside a form does, which would send a
//     half-typed password the moment somebody wanted to check their spelling.
import { useId, useState, type InputHTMLAttributes } from 'react';
import { IconEye, IconEyeOff } from './icons.tsx';

export function PasswordInput({
  className = '',
  wrapperClassName = '',
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { wrapperClassName?: string }) {
  const [shown, setShown] = useState(false);
  const hintId = useId();

  return (
    <span className={`relative block ${wrapperClassName}`}>
      <input
        {...rest}
        type={shown ? 'text' : 'password'}
        // Room for the button, so a long password never runs underneath it.
        className={`${className} pr-11`}
        aria-describedby={rest['aria-describedby'] ?? hintId}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShown((v) => !v)}
        aria-pressed={shown}
        aria-label={shown ? 'Hide password' : 'Show password'}
        title={shown ? 'Hide password' : 'Show password'}
        className="tap absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-lg text-muted transition hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        {shown ? <IconEyeOff size={18} /> : <IconEye size={18} />}
      </button>
      <span id={hintId} className="sr-only">
        {shown ? 'Password is visible on screen' : 'Password is hidden'}
      </span>
    </span>
  );
}
